// @ts-check
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Box,
  Input,
  Button,
  ButtonStyledButton,
  ListMenu,
  ListMenuItem,
  Loader
} from '@codex-brightworld/basics';
import { Icon } from '@codex-brightworld/icons';
import { autocompleteLocations, newPlacesToken, resolveByCountry } from '@codex-brightworld/helpers';

// so that we can use child selectors
const StyledListMenu = styled(ListMenu)``;
const Buttons = styled.div``;

// required by terms of use
const googleMapsBg =
  'url("https://developers.google.com/places/documentation/images/powered-by-google-on-white.png")';

const Wrapper = styled.div`
  position: relative;

  ${Box} {
    border-radius: 2px;
  }

  ${Buttons} {
    position: absolute;
    right: 4px;
    top: 4px;

    ${ButtonStyledButton} {
      height: 36px;
      width: 40px;
      padding: 1px;

      &:first-child {
        border: 0;
      }
    }

    ${Icon} {
      width: 22px;
      height: 22px;
    }
  }

  ${StyledListMenu} {
    position: absolute;
    z-index: 1000;
    width: 100%;
    background: #fff ${googleMapsBg} no-repeat bottom 10px left 10px;
    padding: 0 0 40px 0;

    small {
      color: #aaa;
      display: block;
      text-align: right;
    }

    &::after {
      content: "";
      display: block;
      border-top: 1px solid #d8d8d8;
    }
  }

  ${ListMenuItem} {
    cursor: pointer;
    font-size: 0.9rem;
    color: #666;
    padding: 10px 15px;

    &.active {
      background-color: ${props => props.theme.colors.shades[6]};
    }
  }

  [data-name="loading"] {
    margin-top: 5px;
    margin-right: 5px;
  }
`;

const geoAvailable = 'navigator' in global && 'geolocation' in navigator;

/*
  Spec:
  - [x] show/hide "suggestion box" on focus/blur
    - [x] suggestion box has "powered by google" background
    - [x] floats above the content
  - [x] if the text is empty, show "use my current location" bar
    - [x] clicking it will get current location
      - [ ] or show the note that you need to enable geo API
    - [x] location (lat, long) will passed to onLocate() prop
  - [x] suggest places when typing
    - [x] no loading indicator, new options are just replaced in place
    - [x] results should override previous ones even if they arrived sooner
    - [x] if there were no results, "suggestion box" is hidden
    - [x] clicking "search" button selects the first option (if there is any)
    - [x] clicking one of the options will select it
    - [x] selecting the option means that `value` will be set,
      `onChange` will be fired and suggestions will be hidden (and cleared)
      - [x] selection should not trigger another call
      - [x] selection will generate new token
  - [x] "X" button clears the text and selects `null`
  - [x] there's loader indicator but it's used only for page redirects
        (when something is selected and user is being taken to the store page
        so maybe it's just stateless prop)
*/
function LocationFinder(props) {
  const finder = useLocationFinder(props.onChange, props.onLocate);

  return (
    // prevent mousedown if we are focused
    // (so that blur is not raised and so it won't prevent click events)
    // but allow everything from the input itself, so the user can select text
    <Wrapper
      className={props.className}
      onMouseDown={e => finder.focused && e.target.tagName !== 'INPUT' && e.preventDefault()}
      data-qa={props.qa}
    >
      <Input
        value={(finder.value && finder.value.name) || finder.text}
        interactivePlaceholder
        onFocus={finder.focus}
        onBlur={finder.blur}
        onChange={finder.handleTextInput}
        placeholderText={props.placeholderText}
        onKeyDown={finder.handleKeyDown}
      />

      <Buttons>
        {props.busy ? (
          <Loader spin />
        ) : (
          <>
            <Button onClick={finder.clear} aria-label={props.ariaClear}>
              <Icon icon="close" />
            </Button>
            <Button
              secondary
              onClick={finder.selectFirst}
              aria-label={props.ariaSearch}
            >
              <Icon icon="search" />
            </Button>
          </>
        )}
      </Buttons>

      {finder.showSuggestions && (
        <StyledListMenu items={finder.items}>
          {(item, index) => (
            <ListMenuItem
              key={index}
              className={index === finder.activeItemIndex && 'active'}
              onMouseEnter={() => finder.handleItemHover(index)}
              onClick={() => finder.select(item)}
            >
              <span>{item.name}</span>
              <small>{item.area}</small>
            </ListMenuItem>
          )}
        </StyledListMenu>
      )}

      {finder.showUseMyLocationBar && (
        <StyledListMenu
          items={[
            {
              icon: 'Target',
              label: props.useMyLocationText,
              onClick: finder.locate
            }
          ]}
        />
      )}
    </Wrapper>
  );
}

LocationFinder.propTypes = {
  className: PropTypes.string,
  placeholderText: PropTypes.string,
  useMyLocationText: PropTypes.string,

  /**
   * when place was selected
   * @param {Place} place
   * { name: string, area: string, placeResult: google.maps.places.PlaceResult }
   */
  onChange: PropTypes.func,

  /**
   * when geolocation was used
   * @param {Position} position
   */
  onLocate: PropTypes.func,

  /**
   * show loading indicator (if you need to do something after the change)
   */
  busy: PropTypes.bool,

  /**
   * accessibility labels
   */
  ariaClear: PropTypes.string,
  ariaSearch: PropTypes.string,

  qa: PropTypes.string
};

LocationFinder.defaultProps = {
  className: undefined,
  placeholderText: 'Enter town or postcode',
  useMyLocationText: 'Use my location',
  onChange: () => {},
  onLocate: () => {},
  busy: false,
  qa: undefined,
  ariaClear: 'clear',
  ariaSearch: 'search'
};

export default LocationFinder;

export function useLocationFinder(onChange, onLocate) {
  // useFocus?
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState(null);
  const [text, setText] = useState('');
  const [items, setItems] = useState([]);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const sessionToken = useMemo(newPlacesToken, [value]);

  const clear = () => {
    setValue(null);
    setText('');
  };

  const handleTextInput = (e) => {
    setValue(null);
    setText(e.target.value);
    setActiveItemIndex(-1);
  };

  useEffect(() => {
    if (!text) {
      return;
    }

    // guard against successive requests so that old ones don't overwrite new ones
    let stillPresent = true;

    autocompleteLocations(text, undefined, resolveByCountry(['uk', 'im'], ['ie']), sessionToken)
      .catch(e => [])
      .then((its) => {
        if (stillPresent) {
          setItems(its.map(parsePlace));
        }
      });

    return () => {
      stillPresent = false;
    };
  }, [text]);

  const focus = () => setFocused(true);
  const blur = () => setFocused(false);

  const select = (it) => {
    setValue(it);
    setItems([]);
    onChange(it);
  };

  const selectFirst = () => items.length && select(items[0]);

  const handleItemHover = (index) => {
    setActiveItemIndex(index);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeItemIndex < items.length - 1) {
        setActiveItemIndex(activeItemIndex + 1);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeItemIndex > 0) {
        setActiveItemIndex(activeItemIndex - 1);
      }
    } else if (e.key === 'Enter' && activeItemIndex >= 0) {
      select(items[activeItemIndex]);
    }
  };

  return {
    focused,
    focus,
    blur,
    value,
    text,
    clear,
    handleTextInput,
    sessionToken,
    selectFirst,
    select,
    items,
    activeItemIndex,
    handleItemHover,
    handleKeyDown,

    // only show if the field is focused and there were any results
    showSuggestions: focused && !!items.length && text.length > 0,
    showUseMyLocationBar: geoAvailable && focused && !text,

    locate: () => navigator.geolocation.getCurrentPosition((pos) => {
      onLocate(pos);
    })
  };
}

function parsePlace(placeResult) {
  const terms = placeResult.terms.map(t => t.value);
  const splitIndex = terms.length > 2 ? -2 : -1;

  const res = {
    name: terms.slice(0, splitIndex).join(', '),
    area: terms.slice(splitIndex).join(', '),
    placeResult
  };

  return res;
}
